/**
 * Founding-pilot cohort 2 waitlist admin.
 *
 * Every email that hit the landing-page waitlist form (only rendered when
 * cohort 1 is full) lands in `db.pilot_waitlist`. This page lists them all
 * with fleet size, signup date, and a one-click "Invite to cohort 2" button
 * that fires a warm founder-signed email + stamps the row so the button
 * flips to "Invited N days ago · Nudge" on subsequent clicks.
 *
 * Feeds off:
 *   GET    /api/admin/founding-pilots/waitlist
 *   POST   /api/admin/founding-pilots/waitlist/{id}/invite
 *   DELETE /api/admin/founding-pilots/waitlist/{id}
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Trash, PaperPlaneTilt, ArrowClockwise, MagnifyingGlass, CheckCircle,
  DownloadSimple, BellRinging, Star, Eye, EnvelopeSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

function daysAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

/**
 * Tiny SVG sparkline for the response-rate week-over-week trend.
 * No external chart lib — a polyline over 8 weekly buckets plus a
 * hover tooltip on each point ("Aug 4 · 3/5 · 60%"). Auto-scales
 * to the window max (floor of 1 so a flat zero week renders as a
 * baseline instead of exploding).
 */
function ResponseRateSparkline({ history }) {
  const buckets = history?.buckets || [];
  if (buckets.length < 2) return null;
  const W = 96;
  const H = 26;
  const pad = 3;
  const maxRate = Math.max(1, ...buckets.map((b) => b.response_rate_pct || 0));
  const points = buckets.map((b, i) => {
    const x = pad + (i * (W - pad * 2)) / (buckets.length - 1);
    const y = H - pad - ((b.response_rate_pct || 0) / maxRate) * (H - pad * 2);
    return { ...b, x, y };
  });
  const polyline = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return (
    <div
      className="inline-flex flex-col items-start ml-2"
      data-testid="response-rate-sparkline"
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">
        8-week trend
      </div>
      <svg width={W} height={H} className="text-emerald-300">
        <line
          x1={pad}
          x2={W - pad}
          y1={H - pad}
          y2={H - pad}
          stroke="currentColor"
          strokeOpacity="0.15"
          strokeWidth="1"
          strokeDasharray="2 2"
        />
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          points={polyline}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <circle
            key={p.week_start}
            cx={p.x}
            cy={p.y}
            r={i === points.length - 1 ? 2.2 : 1.6}
            fill="currentColor"
            opacity={p.invited === 0 ? 0.35 : 1}
            data-testid={`response-rate-sparkline-point-${i}`}
          >
            <title>
              {new Date(p.week_start).toLocaleDateString(undefined, {
                month: "short", day: "numeric",
              })} · {p.matched}/{p.invited} · {p.response_rate_pct}%
            </title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

export default function AdminWaitlistPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [invitingId, setInvitingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [testPingBusy, setTestPingBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [weeklyDigestBusy, setWeeklyDigestBusy] = useState(false);
  const [sortByQuality, setSortByQuality] = useState(false);
  const [responseRate, setResponseRate] = useState(null);
  const [responseRateHistory, setResponseRateHistory] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/founding-pilots/waitlist");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load waitlist");
    } finally {
      setLoading(false);
    }
    // Response rate is a separate, non-blocking fetch — a slow/failing
    // response-rate call must never break the main waitlist list.
    try {
      const rr = await client.get("/admin/waitlist/response-rate?window_days=7");
      setResponseRate(rr.data);
    } catch (_) {
      /* silent — the card auto-hides when null */
    }
    // Fetch the week-over-week trend for the mini-chart. Same silent-
    // failure semantics — bad response never breaks the main list.
    try {
      const hist = await client.get(
        "/admin/waitlist/response-rate/history?weeks=8&window_days=7",
      );
      setResponseRateHistory(hist.data);
    } catch (_) {
      /* silent — sparkline auto-hides on null */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    let filtered = data.rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (r) => r.email?.toLowerCase().includes(q) || r.fleet_size?.toLowerCase().includes(q),
      );
    }
    if (sortByQuality) {
      // High-score first; ties fall back to created_at (newer first) for
      // deterministic ordering.
      filtered = [...filtered].sort((a, b) => {
        const diff = (b.quality_score ?? 0) - (a.quality_score ?? 0);
        if (diff !== 0) return diff;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
    }
    return filtered;
  }, [data, search, sortByQuality]);

  const invited = (data?.rows || []).filter((r) => r.cohort2_invited_at).length;
  const pending = (data?.rows || []).length - invited;
  // Rows are already enriched with `quality_score` from the list endpoint.
  // We compute invite-ready count here so the UI doesn't need a 2nd fetch.
  const inviteReady = (data?.rows || []).filter(
    (r) => !r.cohort2_invited_at && (r.quality_score ?? 0) >= 70,
  ).length;

  const invite = async (row) => {
    setInvitingId(row.id);
    const isNudge = !!row.cohort2_invited_at;
    try {
      const r = await client.post(
        `/admin/founding-pilots/waitlist/${row.id}/invite`,
      );
      if (r.data?.status === "sent") {
        toast.success(isNudge
          ? `Nudge sent to ${row.email}`
          : `Invite sent to ${row.email}`);
      } else {
        toast.error(`Send failed: ${r.data?.reason || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setInvitingId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} from the waitlist?`)) return;
    try {
      await client.delete(`/admin/founding-pilots/waitlist/${row.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const bulkInvite = async () => {
    if (!pending) return;
    if (!window.confirm(
      `Send the cohort 2 invite email to all ${pending} pending pilots? This will take ~${Math.max(1, Math.ceil(pending * 0.5))}s.`,
    )) return;
    setBulkBusy(true);
    try {
      const r = await client.post("/admin/founding-pilots/waitlist/bulk-invite");
      const d = r.data || {};
      if (d.failed > 0) {
        toast.warning(`Sent ${d.sent} · ${d.failed} failed. Check the log.`);
      } else if (d.sent === 0) {
        toast.info("Nothing to send — all rows already invited.");
      } else {
        toast.success(`Invited ${d.sent} pilot${d.sent === 1 ? "" : "s"} to cohort 2 🚛`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const topNInvite = async () => {
    if (!pending) return;
    const nStr = window.prompt(
      `Invite the top N highest-scoring pending pilots. How many?`,
      "20",
    );
    if (!nStr) return;
    const n = parseInt(nStr, 10);
    if (isNaN(n) || n < 1 || n > 100) {
      toast.error("Enter a number between 1 and 100");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await client.post(
        "/admin/founding-pilots/waitlist/score-weighted-invite",
        { top_n: n },
      );
      const d = r.data || {};
      if (d.sent === 0) {
        toast.info("Nothing to send.");
      } else {
        toast.success(`Invited top ${d.sent} by quality score 🎯`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-N invite failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const inviteReadyBucket = async () => {
    if (!inviteReady) {
      toast.info("No pending rows meet the quality-70 threshold yet.");
      return;
    }
    if (!window.confirm(
      `Send the cohort 2 invite to all ${inviteReady} pending pilots with a quality score of 70+? ` +
      `This will take ~${Math.max(1, Math.ceil(inviteReady * 0.5))}s.`,
    )) return;
    setBulkBusy(true);
    try {
      const r = await client.post(
        "/admin/founding-pilots/waitlist/invite-ready",
        { min_score: 70 },
      );
      const d = r.data || {};
      if (d.failed > 0) {
        toast.warning(`Sent ${d.sent} · ${d.failed} failed. Check the log.`);
      } else if (d.sent === 0) {
        toast.info("Nothing to send.");
      } else {
        toast.success(`Invited ${d.sent} high-quality pilot${d.sent === 1 ? "" : "s"} ⭐`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite-ready failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = () => {
    // Force a full-page download via the API path — the server responds with
    // `Content-Disposition: attachment` so the browser saves it, doesn't nav.
    // We still hit `client.get` so JWT + refresh handling stay consistent.
    client
      .get("/admin/founding-pilots/waitlist/export.csv", { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        const date = new Date().toISOString().slice(0, 10);
        a.download = `freightpilotai-waitlist-${date}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV downloaded");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Export failed"));
  };

  const sendTestPing = async () => {
    setTestPingBusy(true);
    try {
      const r = await client.post("/admin/waitlist/daily-ping/test");
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success("Test ping sent to Slack ✓");
      } else if (d.status === "skipped_no_webhook") {
        toast.warning(d.reason || "No Slack webhook configured");
      } else {
        toast.error(`Ping failed: ${d.reason || d.slack_body || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test ping failed");
    } finally {
      setTestPingBusy(false);
    }
  };

  const previewInvite = async () => {
    // Ask ops which flavor of the invite email they want to eyeball
    // (first-touch vs nudge). Optional custom recipient — default to the
    // admin's own email (server picks it up from the JWT).
    const nudgeInput = window.prompt(
      "Preview which invite flavor?\n\nType 'nudge' for the follow-up copy, or leave blank for the first-touch invite.",
      "",
    );
    if (nudgeInput === null) return;
    const nudge = (nudgeInput || "").trim().toLowerCase() === "nudge";
    const toEmail = window.prompt(
      "Send the preview to which email? Leave blank to use your admin email.",
      "",
    );
    if (toEmail === null) return;
    setPreviewBusy(true);
    try {
      const r = await client.post(
        "/admin/founding-pilots/waitlist/invite-preview",
        {
          to_email: toEmail.trim() || undefined,
          fleet_size: "5-truck fleet",  // sample fleet so the fleet-line renders
          nudge,
        },
      );
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success(
          `Preview ${nudge ? "nudge" : "invite"} email sent to ${d.to_email} ✓`,
        );
      } else {
        toast.error(`Preview failed: ${d.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewBusy(false);
    }
  };

  const runWeeklyDigest = async () => {
    if (!window.confirm(
      "Force-send the Sunday waitlist digest to ops now?\n\nDoes NOT stamp this week's idempotency row — safe to run repeatedly.",
    )) return;
    setWeeklyDigestBusy(true);
    try {
      const r = await client.post("/admin/waitlist/weekly-digest/preview");
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success(`Weekly digest sent to ${d.recipient} ✓`);
      } else if (d.status === "skipped_no_recipient") {
        toast.warning("No recipient — set WAITLIST_DIGEST_EMAIL or NOTIFY_EMAIL");
      } else {
        toast.error(`Digest failed: ${d.reason || d.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weekly digest failed");
    } finally {
      setWeeklyDigestBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-waitlist-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF9764] mb-1">
            Cohort 2 · Waitlist
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white">
            Pilot waitlist
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Every email captured while cohort 1 was full. One-click invite when cohort 2 opens.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={previewInvite}
            disabled={previewBusy}
            data-testid="waitlist-preview-invite-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60 transition-colors"
          >
            <Eye size={14} weight="bold" />
            {previewBusy ? "Sending…" : "Preview email"}
          </button>
          <button
            onClick={runWeeklyDigest}
            disabled={weeklyDigestBusy}
            data-testid="waitlist-weekly-digest-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60 transition-colors"
          >
            <EnvelopeSimple size={14} weight="bold" />
            {weeklyDigestBusy ? "Sending…" : "Weekly digest"}
          </button>
          <button
            onClick={exportCsv}
            data-testid="waitlist-export-csv-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-300 hover:bg-white/[0.05] transition-colors"
          >
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
          <button
            onClick={sendTestPing}
            disabled={testPingBusy}
            data-testid="waitlist-test-ping-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60 transition-colors"
          >
            <BellRinging size={14} weight="bold" />
            {testPingBusy ? "Sending…" : "Test ping"}
          </button>
          <button
            onClick={load}
            data-testid="waitlist-refresh-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-neutral-300 hover:bg-white/[0.05] transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {pending > 0 && (
        <div
          data-testid="waitlist-bulk-invite-strip"
          className="mb-6 rounded-xl border border-[#FF5A1F]/30 bg-[#FF5A1F]/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
        >
          <div className="text-sm text-neutral-200">
            <span className="tabular-nums font-semibold text-[#FF9764]">{pending}</span> pilot{pending === 1 ? "" : "s"} pending
            {inviteReady > 0 && (
              <>
                {" · "}
                <span
                  className="inline-flex items-center gap-1 text-emerald-300 font-medium"
                  data-testid="waitlist-invite-ready-chip"
                  title="Quality score 70+ — the strongest fleets. Invite these first."
                >
                  <Star size={12} weight="fill" />
                  <span className="tabular-nums">{inviteReady}</span> invite-ready
                </span>
              </>
            )}
            {" — pick your invite strategy."}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {inviteReady > 0 && (
              <button
                onClick={inviteReadyBucket}
                disabled={bulkBusy}
                data-testid="waitlist-invite-ready-btn"
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-sm font-medium px-4 py-2 disabled:opacity-60 transition-colors"
              >
                <Star size={14} weight="fill" />
                Invite ready ({inviteReady})
              </button>
            )}
            <button
              onClick={topNInvite}
              disabled={bulkBusy}
              data-testid="waitlist-topn-invite-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#FF5A1F]/50 bg-transparent hover:bg-[#FF5A1F]/10 text-[#FF9764] text-sm font-medium px-4 py-2 disabled:opacity-60 transition-colors"
            >
              <Star size={14} weight="fill" />
              Invite top N by score
            </button>
            <button
              onClick={bulkInvite}
              disabled={bulkBusy}
              data-testid="waitlist-bulk-invite-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#FF5A1F] hover:bg-[#FF7038] text-black text-sm font-semibold px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {bulkBusy ? "Sending…" : `Send to all ${pending}`}
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3 mb-6">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
            Total on list
          </div>
          <div className="text-2xl font-semibold text-white tabular-nums" data-testid="waitlist-total">
            {data?.count ?? "—"}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
            Pending invite
          </div>
          <div className="text-2xl font-semibold text-[#FF9764] tabular-nums" data-testid="waitlist-pending">
            {pending}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
            Invited
          </div>
          <div className="text-2xl font-semibold text-emerald-300 tabular-nums" data-testid="waitlist-invited">
            {invited}
          </div>
        </div>
      </div>

      <WaitlistHeatmap />

      <SmsInboxPanel />

      {responseRate && responseRate.invited_total > 0 && (
        <div
          data-testid="response-rate-card"
          className="mb-6 rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">
                Response rate · 7d window
              </div>
              <div className="flex items-baseline gap-3">
                <div
                  className={`text-3xl font-semibold tabular-nums ${
                    responseRate.response_rate_pct >= 30 ? "text-emerald-300"
                    : responseRate.response_rate_pct >= 10 ? "text-[#FF9764]"
                    : "text-neutral-400"
                  }`}
                  data-testid="response-rate-pct"
                >
                  {responseRate.response_rate_pct}%
                </div>
                <div className="text-sm text-neutral-400">
                  <span className="tabular-nums text-emerald-200">{responseRate.matched}</span> booked
                  {" · "}
                  <span className="tabular-nums text-white">{responseRate.invited_total}</span> invited
                </div>
                {responseRateHistory?.buckets?.some((b) => b.invited > 0) && (
                  <ResponseRateSparkline history={responseRateHistory} />
                )}
              </div>
              {responseRate.response_rate_pct < 10 && responseRate.window_open < responseRate.invited_total && (
                <div className="mt-2 text-xs text-neutral-500 max-w-xl">
                  Response rate is low — invite copy may need a rewrite. Try the "Preview email" button, then adjust via `PUT /admin/founder-letter`.
                </div>
              )}
              {responseRate.window_open > 0 && (
                <div className="mt-2 text-xs text-neutral-500">
                  <span className="tabular-nums">{responseRate.window_open}</span> invite{responseRate.window_open === 1 ? "" : "s"} still inside their {responseRate.window_days}-day window.
                </div>
              )}
            </div>
            <div className="text-xs text-neutral-500 sm:text-right">
              A "book" = the pilot created a booking OR submitted the founding-pilot / demo contact form after invite.
            </div>
          </div>
          {responseRate.matches?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-[#FF9764] hover:text-[#FF5A1F]">
                Show matches ({responseRate.matches.length})
              </summary>
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {responseRate.matches.map((m, i) => (
                  <li key={i} className="text-xs text-neutral-300 flex items-center gap-2">
                    <CheckCircle size={11} weight="fill" className="text-emerald-400 shrink-0" />
                    <span className="tabular-nums font-medium text-white">{m.email}</span>
                    <span className="text-neutral-500">·</span>
                    <span>{m.name}</span>
                    <span className="text-neutral-500">·</span>
                    <span className="uppercase tracking-wide text-[10px] text-emerald-300">{m.kind}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or fleet size…"
            data-testid="waitlist-search"
            className="w-full rounded-lg bg-white/5 border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/60"
          />
        </div>
        <button
          onClick={() => setSortByQuality((v) => !v)}
          data-testid="waitlist-sort-toggle"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs transition-colors ${
            sortByQuality
              ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF9764]"
              : "border-white/10 bg-white/[0.02] text-neutral-300 hover:bg-white/[0.05]"
          }`}
        >
          <Star size={13} weight={sortByQuality ? "fill" : "bold"} />
          {sortByQuality ? "Sorted by quality" : "Sort by quality"}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="waitlist-empty"
          className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center"
        >
          <div className="text-white font-medium mb-1">
            {data?.count === 0 ? "No one on the waitlist yet" : "No matches for your search"}
          </div>
          <div className="text-sm text-neutral-500">
            {data?.count === 0
              ? "Signups appear here the moment cohort 1 is full and someone joins from the landing page."
              : "Try a different search term."}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="waitlist-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.03] text-xs uppercase tracking-wide text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Fleet</th>
                <th className="text-left px-4 py-3 font-medium">Quality</th>
                <th className="text-left px-4 py-3 font-medium">Joined</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isInvited = !!row.cohort2_invited_at;
                const inviteAgo = daysAgo(row.cohort2_invite_last_sent_at || row.cohort2_invited_at);
                const busy = invitingId === row.id;
                const score = row.quality_score ?? 0;
                const sig = row.quality_signals || {};
                const scoreTone =
                  score >= 70 ? "text-emerald-300"
                  : score >= 40 ? "text-[#FF9764]"
                  : "text-neutral-500";
                const scoreTooltip = (
                  `Fleet ${sig.fleet_pts ?? 0}pt (${sig.fleet_label ?? "?"}) · ` +
                  `Domain ${sig.domain_pts ?? 0}pt (${sig.domain_label ?? "?"}) · ` +
                  `Age ${sig.age_pts ?? 0}pt (${sig.age_days ?? 0}d)`
                );
                return (
                  <tr
                    key={row.id}
                    data-testid={`waitlist-row-${row.id}`}
                    className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3 text-white">{row.email}</td>
                    <td className="px-4 py-3 text-neutral-300">{row.fleet_size || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        title={scoreTooltip}
                        data-testid={`waitlist-quality-${row.id}`}
                        className={`inline-flex items-center gap-1 tabular-nums font-medium ${scoreTone}`}
                      >
                        <Star size={11} weight="fill" /> {score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-400 tabular-nums">{fmt(row.created_at)}</td>
                    <td className="px-4 py-3">
                      {isInvited ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-300">
                          <CheckCircle size={11} weight="fill" /> Invited {inviteAgo}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-2 py-0.5 text-[11px] text-[#FF9764]">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => invite(row)}
                          disabled={busy}
                          data-testid={`waitlist-invite-${row.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-1.5 text-xs text-[#FF9764] hover:bg-[#FF5A1F]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <PaperPlaneTilt size={12} weight="bold" />
                          {busy ? "Sending…" : isInvited ? "Nudge" : "Invite"}
                        </button>
                        <button
                          onClick={() => remove(row)}
                          data-testid={`waitlist-delete-${row.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2 py-1.5 text-xs text-neutral-400 hover:text-red-300 hover:border-red-500/40 transition-colors"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* 90-day signup heatmap — a 7-row × ~13-column calendar grid that surfaces
   weekly patterns ("Mondays get 3× more signups"). Silent when the total
   in-window is 0 so a fresh install doesn't render an empty grid.

   Grouped by week for a compact horizontal read. Each cell's opacity
   scales with `count / max_daily_count` so the visual weight matches the
   raw numbers without needing exact values on-hover to be legible. */
function WaitlistHeatmap() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/admin/waitlist/heatmap?days=90")
      .then((r) => {
        if (!cancelled) setData(r.data || null);
      })
      .catch(() => setData(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || data.total_in_window === 0) return null;

  // Group buckets into weekly columns (Sun-start). Each column becomes 7
  // vertical cells (Mon-Sun) so the eye reads down for the week.
  const columns = [];
  let currentCol = new Array(7).fill(null);
  data.buckets.forEach((b, i) => {
    // Fill up the current column at the correct weekday slot.
    currentCol[b.weekday] = b;
    // Sunday (weekday=6) OR last bucket — close out the column
    if (b.weekday === 6 || i === data.buckets.length - 1) {
      columns.push(currentCol);
      currentCol = new Array(7).fill(null);
    }
  });

  const maxCount = data.max_daily_count || 1;

  // Weekday labels — only rendered on rows 0/3/6 for a lighter visual.
  const dayLabels = ["Mon", "", "", "Thu", "", "", "Sun"];

  return (
    <section
      data-testid="waitlist-heatmap"
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
            Signup heatmap · last {data.window_days} days
          </div>
          <div className="text-sm text-neutral-400 mt-1">
            <span className="tabular-nums text-white font-medium">{data.total_in_window}</span> total ·
            peak day <span className="tabular-nums text-[#FF9764]">{data.max_daily_count}</span>
          </div>
        </div>
        <div className="text-[10px] text-neutral-500 flex items-center gap-1">
          Less
          <div className="flex gap-0.5 mx-1">
            {[0.15, 0.35, 0.55, 0.8, 1].map((o) => (
              <div
                key={o}
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: `rgba(255,90,31,${o})` }}
              />
            ))}
          </div>
          More
        </div>
      </div>

      <div className="flex gap-2 items-start">
        <div className="flex flex-col gap-0.5 pt-0 shrink-0">
          {dayLabels.map((l, i) => (
            <div
              key={i}
              className="h-3 text-[9px] text-neutral-500 leading-3 w-6"
            >
              {l}
            </div>
          ))}
        </div>
        <div
          data-testid="waitlist-heatmap-grid"
          className="flex gap-0.5 flex-1 overflow-x-auto"
        >
          {columns.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-0.5">
              {col.map((cell, ri) => {
                if (!cell) {
                  return <div key={ri} className="h-3 w-3 rounded-sm bg-transparent" />;
                }
                const intensity = cell.count === 0 ? 0 : Math.max(0.15, cell.count / maxCount);
                const bg = cell.count === 0
                  ? "rgba(255,255,255,0.03)"
                  : `rgba(255,90,31,${intensity})`;
                return (
                  <div
                    key={ri}
                    title={`${cell.date} · ${cell.count} signup${cell.count === 1 ? "" : "s"}`}
                    data-testid={`heatmap-cell-${cell.date}`}
                    className="h-3 w-3 rounded-sm"
                    style={{ backgroundColor: bg }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



/**
 * Inbox panel — surfaces every inbound SMS (`pilot_sms_replies` feed)
 * with click-to-reply so ops can respond without leaving `/app/admin/waitlist`.
 *
 * Design:
 *  - Newest-first list capped at 25.
 *  - Each row shows: pilot name (or walk-in phone), quoted body,
 *    received timestamp, inline "invited pilot" / "walk-in" chip.
 *  - Unread badge in the header: red pill with count of invited-pilot
 *    replies where `read_at` is missing. Clears on inbox open.
 *  - Click a row → inline compose textarea drops in with:
 *      · quick-reply template buttons ("Running late", "See you soon", "Reschedule?")
 *      · free-form textarea + send button
 *      · Slack emoji reaction chips (✅ 👍 🕐 ❌ 👀 🔥) for team signal
 *  - Sending POSTs `/admin/sms-replies/{id}/reply` and stamps the row
 *    with the outbound entry, so the full thread stays visible in the
 *    audit trail (rendered under the row as small caption lines).
 */
const QUICK_REPLIES = [
  "Running 5-10 min late, still on for our call.",
  "See you soon — dialling in now.",
  "No problem — want to reschedule? Reply with a better time.",
  "Got it. Talk soon.",
];

// Slack emoji names → button label. Full names match Slack's canonical
// reaction identifiers so the backend can pipe them through untouched.
const REACTION_OPTIONS = [
  { key: "white_check_mark", label: "✅", hint: "Handled" },
  { key: "thumbsup", label: "👍", hint: "Acknowledged" },
  { key: "clock1", label: "🕐", hint: "Follow-up" },
  { key: "eyes", label: "👀", hint: "Looking at it" },
  { key: "fire", label: "🔥", hint: "Hot lead" },
  { key: "x", label: "❌", hint: "Blocked" },
];

// Turn an admin's email into a 1-2 char initials chip. Prefers the local
// part before "@", and if it has separators (`.`, `_`, `-`, `+`) uses the
// first character of the first two tokens. Falls back to first two chars
// of the local part or "?" so the chip never renders empty.
function initialsFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  if (!local) return "?";
  const tokens = local.split(/[._\-+]/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

// Format `read_at` ISO into a compact "read Xs/m/h/d ago" or absolute
// clock time. Kept short for the chip tooltip.
function _readAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

function SmsInboxPanel() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReplyId, setOpenReplyId] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [reactingKey, setReactingKey] = useState(null);
  // `pending` = unread INVITED-pilot replies (walk-ins don't buzz us).
  const unreadInvited = replies.filter(
    (r) => !r.read_at && r.matched_invited_pilot,
  ).length;

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/sms-replies?limit=25");
      setReplies(r.data?.replies || []);
    } catch (_) {
      /* silent — panel auto-hides on empty */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Mark all as read as soon as ops has the panel visible with any
  // unread invited-pilot rows. Debounced with a 1.5s "you actually saw
  // this" delay so we don't burn the badge before the eye can land.
  useEffect(() => {
    if (unreadInvited === 0) return;
    const t = setTimeout(async () => {
      try {
        await client.post("/admin/sms-replies/mark-all-read");
        // Optimistic local update — no need to refetch the whole list.
        setReplies((prev) => prev.map((r) => ({
          ...r,
          read_at: r.read_at || new Date().toISOString(),
        })));
      } catch (_) {
        /* silent */
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [unreadInvited]);

  const toggle = (id) => {
    setOpenReplyId((prev) => (prev === id ? null : id));
    setDraft("");
  };

  const send = async (reply, bodyOverride) => {
    const text = ((bodyOverride ?? draft) || "").trim();
    if (!text) {
      toast.error("Type a reply first");
      return;
    }
    setSending(true);
    try {
      const r = await client.post(
        `/admin/sms-replies/${encodeURIComponent(reply.id)}/reply`,
        { body: text },
      );
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success(`Reply sent to ${d.to_phone} ✓`);
      } else {
        toast.warning(`Reply queued but delivery failed: ${d.reason || "unknown"}`);
      }
      setDraft("");
      setOpenReplyId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const react = async (reply, emoji) => {
    setReactingKey(`${reply.id}-${emoji.key}`);
    try {
      const r = await client.post(
        `/admin/sms-replies/${encodeURIComponent(reply.id)}/react`,
        { emoji: emoji.key },
      );
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success(`Reacted ${emoji.label} in Slack`);
      } else if (d.status?.startsWith("skipped")) {
        toast.info(d.reason || "Reaction not sent");
      } else {
        toast.warning(`Reaction failed: ${d.reason || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reaction failed");
    } finally {
      setReactingKey(null);
    }
  };

  if (loading) return null;
  if (!replies.length) return null;

  return (
    <section
      data-testid="sms-inbox-panel"
      className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              SMS Inbox
            </div>
            <div className="text-sm text-neutral-300">
              <span className="tabular-nums font-semibold text-white">{replies.length}</span>{" "}
              recent {replies.length === 1 ? "reply" : "replies"}
            </div>
          </div>
          {unreadInvited > 0 && (
            <span
              data-testid="sms-inbox-unread-badge"
              className="inline-flex items-center gap-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-200 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 animate-pulse"
              title={`${unreadInvited} unread invited-pilot ${unreadInvited === 1 ? "reply" : "replies"}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
              {unreadInvited} new
            </span>
          )}
        </div>
        <button
          onClick={load}
          data-testid="sms-inbox-refresh"
          className="text-xs text-neutral-500 hover:text-neutral-200"
        >
          Refresh
        </button>
      </div>

      <ul className="space-y-3 max-h-96 overflow-y-auto">
        {replies.map((r) => {
          const isOpen = openReplyId === r.id;
          const isInvited = !!r.matched_invited_pilot;
          const isUnread = !r.read_at && isInvited;
          const ago = r.received_at ? daysAgo(r.received_at) : "";
          const outbound = r.admin_replies || [];
          const reactions = r.slack_reactions || [];
          return (
            <li
              key={r.id}
              data-testid={`sms-inbox-row-${r.id}`}
              className={`rounded-lg border p-3 transition-colors ${
                isOpen
                  ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.04]"
                  : isUnread
                  ? "border-red-500/30 bg-red-500/[0.03]"
                  : "border-white/10 bg-white/[0.02]"
              }`}
            >
              <button
                onClick={() => toggle(r.id)}
                className="w-full text-left"
                data-testid={`sms-inbox-row-toggle-${r.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                      isInvited
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-neutral-500/15 text-neutral-400 border border-neutral-500/30"
                    }`}
                  >
                    {isInvited ? "Invited pilot" : "Walk-in"}
                  </span>
                  <span className="text-xs font-mono text-neutral-400">{r.from_phone}</span>
                  {isUnread && (
                    <span
                      data-testid={`sms-inbox-row-unread-dot-${r.id}`}
                      className="h-1.5 w-1.5 rounded-full bg-red-400"
                    />
                  )}
                  {/* Read-by receipt: initials chip once an admin has marked
                      the row read. Kept small so it doesn't compete with the
                      unread dot. Only shows for invited-pilot rows — walk-in
                      reads aren't tracked as a "who saw it first" signal. */}
                  {!isUnread && isInvited && r.read_by && (
                    <span
                      data-testid={`sms-inbox-row-read-chip-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] border border-white/10 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-neutral-400"
                      title={`Read by ${r.read_by}${r.read_at ? ` · ${_readAgo(r.read_at)}` : ""}`}
                    >
                      <span className="h-1 w-1 rounded-full bg-emerald-400" />
                      {initialsFromEmail(r.read_by)}
                    </span>
                  )}
                  {ago && (
                    <span className="text-xs text-neutral-600 ml-auto">{ago}</span>
                  )}
                </div>
                <div className="text-sm text-white leading-relaxed">
                  <span className="text-neutral-500">&gt; </span>
                  {r.body}
                </div>
              </button>

              {outbound.length > 0 && (
                <div className="mt-2 pl-3 border-l-2 border-white/10 space-y-1">
                  {outbound.map((o) => (
                    <div key={o.id} className="text-xs text-neutral-400">
                      <span className="text-[10px] uppercase tracking-wide text-[#FF9764] mr-1">
                        You:
                      </span>
                      {o.body}
                      {o.sms_status !== "sent" && (
                        <span className="ml-2 text-red-400">· delivery failed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {reactions.length > 0 && (
                <div
                  className="mt-2 flex flex-wrap gap-1"
                  data-testid={`sms-inbox-row-reactions-${r.id}`}
                >
                  {reactions.map((rx, i) => {
                    const meta = REACTION_OPTIONS.find((o) => o.key === rx.emoji);
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] border border-white/10 text-xs px-2 py-0.5"
                        title={`${meta?.hint || rx.emoji} · by ${rx.reacted_by}`}
                      >
                        <span>{meta?.label || `:${rx.emoji}:`}</span>
                      </span>
                    );
                  })}
                </div>
              )}

              {isOpen && (
                <div className="mt-3 space-y-3" data-testid={`sms-inbox-compose-${r.id}`}>
                  {/* Quick-reply templates */}
                  <div
                    className="flex flex-wrap gap-1.5"
                    data-testid={`sms-inbox-quickreplies-${r.id}`}
                  >
                    {QUICK_REPLIES.map((qr, i) => (
                      <button
                        key={i}
                        onClick={() => setDraft(qr)}
                        disabled={sending}
                        data-testid={`sms-inbox-quickreply-${r.id}-${i}`}
                        className="text-xs rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-neutral-300 px-3 py-1 disabled:opacity-50 transition-colors"
                        title="Click to load into textarea, then press Send"
                      >
                        {qr.length > 30 ? qr.slice(0, 28) + "…" : qr}
                      </button>
                    ))}
                  </div>

                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    maxLength={480}
                    placeholder="Type your reply…"
                    data-testid={`sms-inbox-textarea-${r.id}`}
                    className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/60"
                  />

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-neutral-600 tabular-nums">
                      {draft.length}/480
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggle(r.id)}
                        className="text-xs text-neutral-500 hover:text-neutral-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => send(r)}
                        disabled={sending || !draft.trim()}
                        data-testid={`sms-inbox-send-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#FF5A1F] hover:bg-[#FF7038] disabled:opacity-50 disabled:cursor-not-allowed text-black text-xs font-semibold px-3 py-1.5 transition-colors"
                      >
                        {sending ? "Sending…" : "Send SMS"}
                      </button>
                    </div>
                  </div>

                  {/* Slack reaction chips */}
                  <div className="pt-2 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-1.5">
                      React in Slack
                    </div>
                    <div
                      className="flex flex-wrap gap-1"
                      data-testid={`sms-inbox-reactions-${r.id}`}
                    >
                      {REACTION_OPTIONS.map((emoji) => (
                        <button
                          key={emoji.key}
                          onClick={() => react(r, emoji)}
                          disabled={!!reactingKey}
                          data-testid={`sms-inbox-react-${r.id}-${emoji.key}`}
                          title={emoji.hint}
                          className="text-base rounded-full border border-white/10 bg-white/[0.02] hover:bg-white/[0.08] w-8 h-8 flex items-center justify-center disabled:opacity-50 transition-colors"
                        >
                          {reactingKey === `${r.id}-${emoji.key}` ? "…" : emoji.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
